import { GalleryCarouselTypes } from "@app/types/Opensearch.d";
import ArticleCarousel from "@components/Carousel/ArticleCarousel";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import AuthorCard from "@elements/Card/Author/AuthorCard";
import { SocialVariant } from "@elements/ShareSocial/ShareSocial";
import ShareSocialNew from "@elements/ShareSocial/ShareSocialNew";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import Link from "@elements/Typography/Link";
import { StandFirst } from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import {
  findStyleBySectionAndSubCategory,
  isArrayWithElements,
} from "@helper/utils";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import React from "react";

import ArticleElementWrapper from "../Components/ArticleElementWrapper";
import ContentAccessLabel from "../Components/ContentAccessLabel";
import { ShareSocialBar } from "../Components/ShareSocialBar";

export type ArticleContentCardNewProps = {
  mainArticle: ProcessedArticleData;
  latestRegisteredArticle: ProcessedArticleData[];
  pageNumber: number;
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  index?: number;
  visibleArticleId?: string;
};

export default function ArticleContentCardNew({
  mainArticle,
  latestRegisteredArticle,
  pageNumber,
  isInfiniteScrollArticle = false,
  gsChannels,
  articleSlotTarget,
  index,
  visibleArticleId,
}: ArticleContentCardNewProps) {
  const {
    sectionName,
    uniqueSectionName,
    title,
    tags,
    authors,
    date,
    dateCreated,
    time,
    articleBody,
    standFirst,
    contentAccess,
    urlPath,
    imageGallery,
    relatedArticles,
    attachments,
    id,
    linkToSection,
    adsUniqueSectionName,
  } = mainArticle;
  const socialIconList = ["facebook", "telegram", "plainWhatsapp", "newsPost"];
  const pageUrl = `${urlPath}`;
  const renderItems = imageGallery.filter(
    (item) => item.type !== GalleryCarouselTypes.UNKNOWN,
  );
  const sectionColor = mainArticle.parentSection
    ? findStyleBySectionAndSubCategory(mainArticle.parentSection)
        ?.selectedColor || ""
    : findStyleBySectionAndSubCategory(sectionName || "")?.selectedColor || "";

  return (
    <React.Fragment key={index}>
      <ArticleElementWrapper>
        <div
          className={`flex items-center justify-between pb-3 mb-3 border-b-2 border-${sectionColor}`}
        >
          <SectionTitleV2
            Element={Link}
            elementLink={linkToSection}
            className="text-[16px] lg:text-[12px] lg:font-semibold mb-0 leading-[120%]"
            sectionName={sectionName}
            parentSection={mainArticle.parentSection}
            iconStyle="hidden"
          />
          <ShareSocialBar
            dateCreated={dateCreated}
            pageUrl={pageUrl}
            socialIconList={socialIconList}
            title={title}
          />
        </div>
        <div className="flex flex-col gap-[5px] mb-3">
          <ContentAccessLabel contentAccess={contentAccess} showIcon={false} />
          <Heading
            Element="h1"
            className="font-secondary leading-[120%] text-black-800 text-[22px] lg:text-[38px] font-medium"
          >
            {title}
          </Heading>
          {standFirst && (
            <StandFirst className="text-xs lg:text-xs font-primary font-[500] leading-[150%] text-grey-350">
              {standFirst}
            </StandFirst>
          )}
        </div>
        {/* Tags dont dont render If alias not available in payload*/}
        <RenderTags
          tagsWrapper="my-3 gap-[10px]"
          tags={tags}
          tagClass="bg-grey-1000 px-[10px] py-[3px] not-italic text-[12px] lg:text-[12px] leading-[150%] text-grey-350"
        />
        <div className="flex mb-3">
          <Date
            date={date}
            time={time}
            variant={"or"}
            className="text-[14px] font-semimedium leading-[150%] text-black-700"
          />
        </div>
        {isArrayWithElements(authors) && (
          <div className="flex flex-row gap-3 flex-wrap mb-3">
            {authors.map(
              (author, index) =>
                author.hasProfile && (
                  <AuthorCard
                    key={index}
                    author={author}
                    displayHeight={38}
                    displayWidth={38}
                    imageWrapper="w-[48px] p-[5px]"
                    authorCardWrapper="gap-[5px]"
                    nameClass="text-[12px] lg:text-[12px] font-medium text-blue-400 leading-[150%]"
                    hasEmail={false}
                    forAuthorListing={false}
                  />
                ),
            )}
          </div>
        )}
        {isArrayWithElements(renderItems) && (
          <ArticleCarousel
            renderItems={renderItems}
            sectionName={sectionName}
            wrapperClass="max-w-[860px]"
            newDesign={true}
          />
        )}
      </ArticleElementWrapper>

      <ArticleParagraph
        freeArticle={mainArticle}
        articleId={id}
        contentAccess={contentAccess}
        articleBody={articleBody}
        uniqueSectionName={uniqueSectionName}
        relatedArticles={relatedArticles}
        latestRegisteredArticle={latestRegisteredArticle}
        pageNumber={pageNumber}
        isInfiniteScrollArticle={isInfiniteScrollArticle}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        visibleArticleId={visibleArticleId}
        adsUniqueSectionName={adsUniqueSectionName}
        newDesign={true}
        attachments={attachments}
        tags={tags}
      />

      <ArticleElementWrapper>
        <div className="flex self-end items-center justify-end my-6 lg:my-8">
          {socialIconList.map((variant, index) => (
            <ShareSocialNew
              variant={variant as SocialVariant}
              pagePath={pageUrl}
              pageTitle={title}
              dateCreated={dateCreated}
              key={index}
            />
          ))}
        </div>
      </ArticleElementWrapper>
    </React.Fragment>
  );
}
